import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { kycDSPV2Status } from '@screens/KYC/KYCDocuments/services/kycDocumentsService';
import { KYC_RESPONSE_STATUS, KYC_ERROR_MAP } from "@screens/KYC/utils";
import { removeUrlWithoutReload } from "@app/utils";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { PrivateStackParamList } from "@app/navigationApp/types";
import { updateLinkedApplication } from "@app/store/slices/authSlice";
import handleApiErr from '@app/helper/handleApiErr';
import { showErrorSheet } from '@app/store/slices/errorSlice';

export const useKYCRedirectHandler = (
  navigation: NativeStackNavigationProp<PrivateStackParamList, 'KYC_DIGIO_REDIRECT'>,
  routeName: string
) => {
  const userDetails = useAppSelector(state => state.auth.userDetails);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // removeUrlWithoutReload("kyc_redirect");
    
    const fetchKYCRedirectStatus = async () => {
      let errorConfig;
      let showSuccessCard = false;
      let showFailureCard = false;
      let failedTitle = undefined;
      let failedSubTitle = undefined;
      let currentStepId = "KYC_DOCUMENTS";
      
      try {
        const applicationId = userDetails?.linkedApplications[0]?.applicationId;
        if (!applicationId) return;
        
        const response = await kycDSPV2Status({ applicationId });

        const stepResponseObject = response?.data?.stepResponseObject;
        currentStepId = response?.data?.updatedApplicationObj?.currentStepId;
        
        if (stepResponseObject === KYC_RESPONSE_STATUS.COMPLETED) {
          showSuccessCard = true;
          currentStepId = response?.data?.updatedApplicationObj?.currentStepId;
          dispatch(updateLinkedApplication(response?.data?.updatedApplicationObj));
        }
        else if (stepResponseObject === KYC_RESPONSE_STATUS.CREATED) {
          showFailureCard = true;
          errorConfig = KYC_ERROR_MAP[stepResponseObject] || KYC_ERROR_MAP.DEFAULT;
          failedTitle = 'KYC verification canceled';
          failedSubTitle = 'KYC verification is required for the completion of your loan application';
        }
        else {
          showFailureCard = true;
          errorConfig = KYC_ERROR_MAP[stepResponseObject] || KYC_ERROR_MAP.DEFAULT;
          failedTitle = errorConfig.failedTitle;
          failedSubTitle = errorConfig.failedSubTitle;
        }
      } catch (error: any) {
        console.error('Error calling kycDSPV2Status:', error);
        
        if (handleApiErr(error)) {
          dispatch(showErrorSheet({
            isErrorSheetVisible: true,
            errorMessage: error?.data?.message,
            closeOnBackdropPress: true,
            errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
            routeName: routeName,
            errorHeading: error?.data?.header,
            ctaLabel: 'Ok, got it'
          }));
        }
        
        const voltErrorCode = error?.data?.voltErrorCode;
        if (voltErrorCode === KYC_RESPONSE_STATUS.MISMATCH_DETECTED) {
          showFailureCard = true;
          errorConfig = KYC_ERROR_MAP[voltErrorCode] || KYC_ERROR_MAP.DEFAULT;
          failedTitle = errorConfig.failedTitle;
          failedSubTitle = error?.data?.message;
        } else {
          showFailureCard = true;
          errorConfig = KYC_ERROR_MAP[voltErrorCode] || KYC_ERROR_MAP.DEFAULT;
          failedTitle = errorConfig.failedTitle;
          failedSubTitle = errorConfig.failedSubTitle;
        }
      }
      
      navigation.navigate("KYC_DOCUMENTS", {
        showSuccessCard,
        showFailureCard,
        failedTitle,
        failedSubTitle,
        currentStepId,
      });
    };
    
    fetchKYCRedirectStatus();
  }, [navigation, userDetails, dispatch, routeName]);
}; 